import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, inr, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { SectionHead } from "../components/Motion";
import ProductCard from "../components/ProductCard";
import NotifyMe from "../components/NotifyMe";
import AddressForm, { EMPTY_ADDRESS, validateAddress } from "../components/AddressForm";
import { LogOut, ShieldCheck, Plus, Pencil, Trash2, Star, BadgeCheck, X } from "lucide-react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";

const input = "mt-1.5 w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F] placeholder:text-[#857078]/50";

export default function Account() {
  const { user, logout, wishlist, setUser } = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const [orders, setOrders] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [addrForm, setAddrForm] = useState(null);

  useEffect(() => { if (user === null) navigate("/login"); }, [user]);

  const loadAddresses = () => api.get("/customer/addresses").then((r) => setAddresses(r.data.addresses)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name || "", phone: user.phone || "" });
    api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
    api.get("/products").then((r) => setWishlistProducts(r.data.products.filter((p) => wishlist.includes(p.slug)))).catch(() => {});
    loadAddresses();
  }, [user, wishlist]);

  if (!user) return null;

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/customer/profile", profile);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveAddress = async () => {
    const err = validateAddress(addrForm);
    if (err) return toast.error(err);
    try {
      if (addrForm.id) await api.patch(`/customer/addresses/${addrForm.id}`, addrForm);
      else await api.post("/customer/addresses", addrForm);
      toast.success("Address saved");
      setAddrForm(null);
      loadAddresses();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const TABS = [["profile", "Profile"], ["addresses", "Saved Addresses"], ["orders", "Orders"], ["wishlist", "Wishlist"]];

  return (
    <div className="pt-28 pb-24 max-w-5xl mx-auto px-4 sm:px-6 min-h-screen" data-testid="account-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <SectionHead overline={`My Account · ${user.customer_id || ""}`} title={`Hello, ${user.name.split(" ")[0]}`} sub={user.email} />
        <div className="flex gap-3">
          {user.role === "admin" && (
            <Link to="/admin" className="flex items-center gap-2 text-xs tracking-wider uppercase border border-[#7E1E3F]/40 text-[#5C1230] rounded-full px-5 py-2.5 hover:bg-[#7E1E3F]/10" data-testid="admin-dashboard-link">
              <ShieldCheck size={14} /> Admin
            </Link>
          )}
          <button onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-2 text-xs tracking-wider uppercase text-[#857078] hover:text-[#E63946] transition-colors" data-testid="logout-button">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#7E1E3F]/15 mb-8 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setParams({ tab: k })}
            className={`px-5 py-3 text-xs tracking-[0.15em] uppercase whitespace-nowrap transition-colors ${tab === k ? "text-[#7E1E3F] border-b-2 border-[#7E1E3F] font-semibold" : "text-[#857078] hover:text-[#2E1A22]"}`}
            data-testid={`account-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="profile-section">
          <form onSubmit={saveProfile} className="card-surface rounded-2xl p-6 space-y-4">
            <h3 className="font-serif-lux text-xl text-[#2E1A22]">Personal Information</h3>
            <label className="block"><span className="text-xs font-mono-lux tracking-[0.12em] uppercase text-[#857078]">Full Name</span>
              <input className={input} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" /></label>
            <label className="block"><span className="text-xs font-mono-lux tracking-[0.12em] uppercase text-[#857078]">Mobile Number</span>
              <div className="flex">
                <span className="mt-1.5 bg-[#FBF7F1] border border-r-0 border-[#E5E7EB] rounded-l-xl px-3 py-3 text-sm text-[#857078]">+91</span>
                <input className={`${input} rounded-l-none`} inputMode="numeric" maxLength={10} value={profile.phone} placeholder="10-digit mobile"
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="profile-phone" /></div>
            </label>
            <button className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-6 py-3 hover:bg-[#5C1230] transition-colors" data-testid="profile-save">Save Changes</button>
          </form>
          <div className="card-surface rounded-2xl p-6 space-y-4" data-testid="account-info">
            <h3 className="font-serif-lux text-xl text-[#2E1A22]">Account Details</h3>
            {[["Customer ID", user.customer_id || "—"], ["Email", user.email], ["Sign-in Method", user.auth_method === "google" ? "Google" : "Email & Password"],
              ["Member Since", user.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm border-b border-[#7E1E3F]/8 pb-2.5">
                <span className="text-[#857078]">{k}</span><span className="text-[#2E1A22] font-medium">{v}</span>
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <span className={`text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 ${user.email_verified ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#E5A93C]/15 text-[#B07C0F]"}`} data-testid="email-verified-badge">
                <BadgeCheck size={11} /> Email {user.email_verified ? "Verified" : "Unverified"}
              </span>
              <span className={`text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 ${user.phone_verified ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#E5E7EB] text-[#857078]"}`} data-testid="phone-verified-badge">
                <BadgeCheck size={11} /> Phone {user.phone_verified ? "Verified" : "Not verified"}
              </span>
            </div>
          </div>
        </div>
      )}

      {tab === "addresses" && (
        <div data-testid="addresses-section">
          <div className="flex justify-between items-center mb-5">
            <p className="text-sm text-[#857078]">{addresses.length} saved address{addresses.length === 1 ? "" : "es"}</p>
            <button onClick={() => setAddrForm({ ...EMPTY_ADDRESS })} className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-5 py-2.5 hover:bg-[#5C1230] transition-colors" data-testid="add-address-button">
              <Plus size={13} /> Add New Address
            </button>
          </div>
          {addresses.length === 0 ? (
            <div className="text-center py-14 card-surface rounded-2xl" data-testid="addresses-empty">
              <p className="font-serif-lux text-xl text-[#857078]">No saved addresses yet</p>
              <p className="text-sm text-[#857078] mt-2">Save one now — checkout becomes one tap.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {addresses.map((ad) => (
                <div key={ad.id} className={`card-surface rounded-2xl p-5 relative ${ad.is_default ? "border-[#7E1E3F]/50" : ""}`} data-testid={`address-card-${ad.id}`}>
                  {ad.is_default && <span className="absolute top-4 right-4 text-[10px] font-mono-lux tracking-widest uppercase bg-[#7E1E3F] text-white px-2.5 py-1 rounded-full flex items-center gap-1"><Star size={9} /> Default</span>}
                  <p className="font-medium text-[#2E1A22]">{ad.name}</p>
                  <p className="text-xs text-[#857078] mt-0.5">+91 {ad.phone}</p>
                  <p className="text-sm text-[#45303A] mt-2 leading-relaxed">{ad.house}, {ad.street}{ad.landmark ? `, ${ad.landmark}` : ""}, {ad.city}, {ad.district}, {ad.state} – {ad.pincode}</p>
                  <div className="flex gap-2 mt-4">
                    {!ad.is_default && (
                      <button onClick={async () => { await api.post(`/customer/addresses/${ad.id}/default`); loadAddresses(); }}
                        className="text-[11px] border border-[#7E1E3F]/40 text-[#7E1E3F] rounded-full px-3 py-1.5 hover:bg-[#7E1E3F]/8" data-testid={`address-default-${ad.id}`}>Set Default</button>
                    )}
                    <button onClick={() => setAddrForm(ad)} className="text-[11px] border border-[#E5E7EB] text-[#45303A] rounded-full px-3 py-1.5 hover:border-[#7E1E3F] flex items-center gap-1" data-testid={`address-edit-${ad.id}`}><Pencil size={10} /> Edit</button>
                    <button onClick={async () => { if (window.confirm("Delete this address?")) { await api.delete(`/customer/addresses/${ad.id}`); loadAddresses(); } }}
                      className="text-[11px] border border-[#E63946]/40 text-[#E63946] rounded-full px-3 py-1.5 hover:bg-[#E63946]/8 flex items-center gap-1" data-testid={`address-delete-${ad.id}`}><Trash2 size={10} /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "orders" && (
        orders.length === 0 ? (
          <div className="text-center py-16 card-surface rounded-2xl" data-testid="orders-empty">
            <p className="font-serif-lux text-xl text-[#857078]">No orders yet</p>
            <Link to="/gifts" className="inline-block mt-5 text-xs tracking-[0.15em] uppercase font-semibold bg-[#7E1E3F] text-white rounded-full px-6 py-3">Find a Gift</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.order_id} className="card-surface rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid={`order-row-${o.order_id}`}>
                <img src={o.items[0]?.image} alt="" className="w-14 h-16 object-contain bg-white rounded-lg border border-[#E5E7EB]" />
                <div className="flex-1 min-w-[180px]">
                  <p className="font-mono-lux text-sm text-[#7E1E3F]">{o.order_id}</p>
                  <p className="text-xs text-[#857078] mt-0.5">{o.items.map((i) => `${i.name} × ${i.qty}`).join(", ")}</p>
                  <p className="text-[11px] text-[#857078] mt-0.5">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#2E1A22] font-semibold">{inr(o.total)}</p>
                  <p className="text-xs text-[#5C1230] mt-0.5">{o.status}</p>
                </div>
                <Link to={`/order/${o.order_id}`} state={{ email: o.email }} className="text-xs tracking-wider uppercase border border-[#7E1E3F]/40 text-[#5C1230] rounded-full px-4 py-2 hover:bg-[#7E1E3F]/10" data-testid={`view-order-${o.order_id}`}>
                  View
                </Link>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "wishlist" && (
        wishlistProducts.length === 0 ? (
          <div className="text-center py-16 card-surface rounded-2xl" data-testid="wishlist-empty">
            <p className="font-serif-lux text-xl text-[#857078]">Your wishlist is empty</p>
            <p className="text-sm text-[#857078] mt-2">Tap the heart on any product to save it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wishlist-grid">
            {wishlistProducts.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onNotify={setNotifyProduct} />)}
          </div>
        )
      )}

      <AnimatePresence>
        {addrForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm overflow-y-auto" onClick={() => setAddrForm(null)} data-testid="address-modal">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              className="max-w-xl mx-auto my-10 bg-white rounded-2xl shadow-2xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif-lux text-2xl text-[#2E1A22]">{addrForm.id ? "Edit Address" : "New Address"}</h3>
                <button onClick={() => setAddrForm(null)} className="text-[#857078]" data-testid="address-modal-close"><X size={20} /></button>
              </div>
              <AddressForm value={addrForm} onChange={setAddrForm} />
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setAddrForm(null)} className="text-xs font-semibold tracking-wider uppercase border border-[#E5E7EB] text-[#45303A] rounded-full px-6 py-3" data-testid="address-cancel">Cancel</button>
                <button onClick={saveAddress} className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-8 py-3 hover:bg-[#5C1230] transition-colors" data-testid="address-save">Save Address</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <NotifyMe product={notifyProduct} onClose={() => setNotifyProduct(null)} />
    </div>
  );
}
